// Catalog.ID Client — Consent-Requester Mode Handlers
// ============================================================================
// Per-mode dispatch for the `registry-request` envelopes sent by the modules
// that run a back-channel OIDC flow — the desktop service and, since publisher
// M5, the publisher module. The dispatcher in `client.ts` routes here on
// `requesterId`; ALLOWED_REQUESTERS below is the matching gate. (The file name
// is historical: the desktop service was the only such requester in v1.)
//
// Inbound envelopes have already been signature-verified by the cld-id
// server's relay (`KeyGrantRelay.TryRejectUnverifiedRegistryRequestAsync`).
// Browser code does not re-verify; trust is anchored on the relay's verdict.
//
// Modes:
//   - `oidc-identity` (docs/cid-governance.md M7b): which identity will
//     consent? The module authorizes at that identity's ORIGIN with
//     `login_hint`, so it asks first; answered with the active CID once the
//     tab is signed in and unlocked.
//   - `oidc-consent`: open the OIDC consent MODAL over the CID module for the
//     given authorization `request_id` and return the {code, state, iss} the
//     approve API yields, or a denied/timeout result. See desktop-types.ts for
//     the flow context. (It was a pane until 2026-09-10; the pane type is gone.)
// ============================================================================

import {
    registerRelayConsent, cancelRelayConsent, hasRelayConsent,
} from './pending-consent';
import type { DesktopRequest, DesktopResponse } from './desktop-types';
import { ensureUnlocked, requestTabFocus } from './unlock-gate';

type ResponseBase = Pick<DesktopResponse, 'type' | 'to' | 'requestId'>;

/**
 * Cap on how long the opened consent pane may sit unanswered before the
 * handler gives up. Matches the desktop caller's per-request budget for this
 * mode (10 min) — the caller usually times out first; this bound exists so an
 * abandoned pane cannot leak the pending-store entry forever.
 */
const CONSENT_WAIT_TIMEOUT_MS = 600_000;

/**
 * Modules allowed to drive the consent pane through this handler. Each entry is
 * a hub service id whose envelopes the cld-id relay signature-verifies before
 * the browser sees them, so this list is a policy gate, not an authentication
 * one. Keep it in step with the dispatcher branch in `client.ts` and the
 * `requesterId` union in `desktop-types.ts`.
 */
const ALLOWED_REQUESTERS: readonly string[] = ['desktop', 'publisher', 'work'];

/**
 * The desktop service's own first-party OIDC client (`CidOidcOptions.ClientId`
 * on the desktop side). The ONLY client a consent may be approved silently for.
 */
export const DESKTOP_CLIENT_ID = 'catalog-desktop-client';

/** What the desktop's sign-in asks for, and all a silent approval may grant. */
const DESKTOP_SCOPES: readonly string[] = ['openid', 'profile'];

/** The parts of a pending authorization the silent path checks (from the node). */
export interface AuthorizationSummary {
    clientId: string;
    scopes: string[];
    /** The relying party asked for private claims (the dialog's checkboxes). */
    claimsRequested: boolean;
}

/**
 * May this authorization be approved without a dialog? Only the desktop's own
 * client, asking for no more than the desktop's sign-in ever asks for. Anything
 * else is somebody else's request and gets the dialog, ticket or not.
 */
export function qualifiesForSilentConsent(summary: AuthorizationSummary): boolean {
    return summary.clientId === DESKTOP_CLIENT_ID
        && !summary.claimsRequested
        && summary.scopes.every(scope => DESKTOP_SCOPES.includes(scope));
}

export interface DesktopDispatchDeps {
    /** Returns the active identity's cid, or null if no identity is active. */
    getActiveCid: () => string | null;
    /** Opens the OIDC consent UI (a modal over the CID module) for the given
     *  authorization request id. The consent surface reports back through the
     *  pending-consent store. */
    openConsentUi: (oidcRequestId: string) => void;
    /** Test seam; defaults to {@link CONSENT_WAIT_TIMEOUT_MS}. */
    consentWaitTimeoutMs?: number;
    /** Test seam; production uses the shared
     *  `unlock-gate.UNLOCK_WAIT_TIMEOUT_MS` (5 min). */
    unlockWaitTimeoutMs?: number;
    /** Ask the hub to raise the CID tab for this request (M2c). Test seam;
     *  production uses the socket registered by `key-grant/client.ts`. */
    requestFocus?: (requestId: string) => void;

    // ── Silent consent after this tab's own login (decision 1) ──────────
    // All three absent ⇒ every consent shows the dialog, as before.

    /** Spend this tab's fresh-login ticket (`state/tab-session.ts`). True only
     *  for the current, unused ticket of `cid`, inside its window. */
    consumeLoginTicket?: (cid: string, ticket: string | undefined) => boolean;
    /** The pending authorization's client and asks, from the node — never
     *  from the envelope. */
    getAuthorization?: (oidcRequestId: string) => Promise<AuthorizationSummary>;
    /** Approve with the same step-up proof the dialog's Approve button uses. */
    approveWithoutDialog?: (
        oidcRequestId: string, scopes: string[],
    ) => Promise<{ code: string; state: string; iss: string }>;
}

export async function dispatchDesktopRequest(
    request: DesktopRequest,
    deps: DesktopDispatchDeps,
): Promise<DesktopResponse> {
    const base: ResponseBase = {
        type: 'registry-response',
        to: request.requesterId,
        requestId: request.requestId,
    };

    if (!request._senderServiceId || request._senderServiceId !== request.requesterId) {
        return { ...base, status: 'unavailable', reason: 'sender-mismatch' };
    }
    if (!ALLOWED_REQUESTERS.includes(request.requesterId)) {
        return { ...base, status: 'unavailable', reason: 'unsupported-requester' };
    }

    switch (request.mode) {
        case 'oidc-consent':
            return await handleOidcConsent(request, base, deps);
        case 'oidc-identity':
            return await handleOidcIdentity(request, base, deps);
        default:
            return { ...base, status: 'unavailable', reason: 'unsupported-mode' };
    }
}

// ── oidc-consent ────────────────────────────────────────────────────────
//
// Gate order: unlock-wait runs BEFORE the identity check — on the rare
// in-flight-logout race the bag and identity clear together, and waiting for
// the next load gives both a chance to come back before we give a verdict.
async function handleOidcConsent(
    request: DesktopRequest,
    base: ResponseBase,
    deps: DesktopDispatchDeps,
): Promise<DesktopResponse> {
    const oidcRequestId = request.oidcRequestId;
    if (!oidcRequestId) {
        return { ...base, status: 'unavailable', reason: 'missing-required-field' };
    }

    // Wait-for-unlock, same as every other family since M2c: a locked bag
    // raises this tab and waits, whatever the sender's flag says. In practice
    // a locked bag here is a rare race anyway (the keygrant WS only connects
    // after `initShell`, i.e. post-unlock) — a logout while the envelope was
    // in flight. The BEFORE-delivery focus this mode needs is separate and
    // still comes from the desktop caller's `requiresUserInteraction:true`:
    // `oidc-consent` opens a pane even on an unlocked tab.
    if (!await ensureUnlocked(request, deps)) {
        return { ...base, status: 'unavailable', reason: 'cid-locked' };
    }

    const cid = deps.getActiveCid();
    if (!cid) return { ...base, status: 'unavailable', reason: 'no-active-identity' };

    if (hasRelayConsent(oidcRequestId)) {
        // A consent pane for this authorization request is already open and
        // owned by an earlier envelope; a second concurrent claim would steal
        // its resolver.
        return { ...base, status: 'unavailable', reason: 'duplicate-request' };
    }

    // Claim the authorization BEFORE anything is awaited: the duplicate check
    // above and this claim must not have a gap a second envelope could use.
    const resultPromise = registerRelayConsent(oidcRequestId);

    const silent = await trySilentConsent(request, oidcRequestId, cid, deps);
    if (silent) {
        cancelRelayConsent(oidcRequestId);
        return { ...base, status: 'approved', code: silent.code, state: silent.state, iss: silent.iss };
    }

    // The dialog. A request that did not ask the hub to focus this tab first
    // (the desktop's follow-up sign-in, which expected to go silent) asks now:
    // a dialog behind another panel is a dialog nobody answers.
    if (request.requiresUserInteraction !== true) {
        requestTabFocus(request.requestId, deps.requestFocus);
    }

    deps.openConsentUi(oidcRequestId);

    const consentTimeoutMs = deps.consentWaitTimeoutMs ?? CONSENT_WAIT_TIMEOUT_MS;
    const result = await Promise.race([
        resultPromise,
        new Promise<null>(resolve => setTimeout(() => resolve(null), consentTimeoutMs)),
    ]);

    if (result === null) {
        cancelRelayConsent(oidcRequestId);
        return { ...base, status: 'unavailable', reason: 'consent-timeout' };
    }
    if (result.outcome === 'denied') {
        return { ...base, status: 'denied', reason: 'user-declined' };
    }
    return { ...base, status: 'approved', code: result.code, state: result.state, iss: result.iss };
}

/**
 * The silent path (decision 1, 2026-10-08): the dashboard follows a login in
 * this tab by signing in itself, and that consent is approved without a
 * dialog — under ALL of these, checked in this order:
 *
 *   1. the requester is the desktop service (hub-attested), never publisher
 *      or work, whose sign-ins are their own business;
 *   2. it presents the ticket this tab minted at its own interactive login,
 *      for the active account, unused and inside its one-minute window — the
 *      ticket is spent by the attempt;
 *   3. the node says the authorization is for the desktop's own client and
 *      asks for nothing beyond `openid profile` and no private claims.
 *
 * Any miss — including a failing approval — returns null and the dialog runs.
 */
async function trySilentConsent(
    request: DesktopRequest,
    oidcRequestId: string,
    cid: string,
    deps: DesktopDispatchDeps,
): Promise<{ code: string; state: string; iss: string } | null> {
    if (request.requesterId !== 'desktop') return null;
    if (!deps.consumeLoginTicket || !deps.getAuthorization || !deps.approveWithoutDialog) return null;
    if (!request.loginTicket) return null;
    if (!deps.consumeLoginTicket(cid, request.loginTicket)) return null;
    try {
        const summary = await deps.getAuthorization(oidcRequestId);
        if (!qualifiesForSilentConsent(summary)) return null;
        return await deps.approveWithoutDialog(oidcRequestId, summary.scopes);
    } catch {
        return null;
    }
}

// ── oidc-identity ───────────────────────────────────────────────────────
//
// docs/cid-governance.md M7b: sessions, consent and codes all live at the
// account's origin, so a module authorizes THERE, with `login_hint` — and must
// learn which identity will consent before it starts. Same gates as consent
// (the unlock wait raises the tab so the member can sign in), then the active
// identity's CID. Nothing is opened and nothing is signed: the CID of the
// active identity is what the consent screen would show anyway.
async function handleOidcIdentity(
    request: DesktopRequest,
    base: ResponseBase,
    deps: DesktopDispatchDeps,
): Promise<DesktopResponse> {
    if (!await ensureUnlocked(request, deps)) {
        return { ...base, status: 'unavailable', reason: 'cid-locked' };
    }
    const cid = deps.getActiveCid();
    if (!cid) return { ...base, status: 'unavailable', reason: 'no-active-identity' };
    return { ...base, status: 'approved', cid };
}
